/**
 * Compare Runs page. Lines up 2–4 benchmark runs by case and surfaces every metric, output,
 * judge verdict, and the input itself for visual adjudication.
 *
 * URL-driven (shareable): ?runs=<id,id>&baseline=<id>&tab=overview. Renders full-bleed and
 * offers a CSS pseudo-fullscreen (not the native API, which would z-index antd portals under
 * the backdrop). Heavy per-case output is fetched lazily by the drawer, not here.
 */
import { useState } from "react";
import { useQueryState, parseAsArrayOf, parseAsString } from "nuqs";
import { Tabs, Radio, Button, Alert, Empty, Spin, Tag } from "antd";
import {
  BarChartOutlined,
  TableOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Topbar } from "@/components/layout/topbar";
import { Content } from "@/components/layout/content";
import { useCompareData } from "./hooks/use-compare-data";
import { CompareRunPicker } from "./components/CompareRunPicker";
import { CompareOverview } from "./components/CompareOverview";
import { CompareCasesTable } from "./components/CompareCasesTable";
import { useQuery } from "@tanstack/react-query";
import { fetchAgents } from "@/lib/api/resources/benchmark";

export default function BenchmarkCompare() {
  const [runIds, setRunIds] = useQueryState(
    "runs",
    parseAsArrayOf(parseAsString).withDefault([]),
  );
  const [baselineParam, setBaselineParam] = useQueryState(
    "baseline",
    parseAsString,
  );
  const [tab, setTab] = useQueryState(
    "tab",
    parseAsString.withDefault("overview"),
  );
  const [fullscreen, setFullscreen] = useState(false);

  const { runs, matrix, compatibility, loading, missingIds } =
    useCompareData(runIds);
  const { data: agentsData } = useQuery({
    queryKey: ["agents_list"],
    queryFn: () => fetchAgents(),
  });
  const agents =
    agentsData?.member || (agentsData as any)?.["hydra:member"] || [];

  const baselineId =
    runs.find((r) => r.id === baselineParam)?.id ?? runs[0]?.id ?? "";

  const ready = runs.length >= 2;

  const toolbar = (
    <div className="space-y-3">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-grow min-w-[20rem]">
          <CompareRunPicker
            selectedIds={runIds}
            onChange={(ids) => setRunIds(ids)}
          />
        </div>
        <Button
          icon={
            fullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />
          }
          onClick={() => setFullscreen((v) => !v)}
        >
          {fullscreen ? "Exit fullscreen" : "Fullscreen"}
        </Button>
      </div>

      {ready && (
        <div className="flex items-center gap-3 flex-wrap text-sm">
          <span className="text-gray-500">
            Baseline (deltas compare against):
          </span>
          <Radio.Group
            optionType="button"
            size="small"
            value={baselineId}
            onChange={(e) => setBaselineParam(e.target.value)}
          >
            {runs.map((r) => (
              <Radio.Button key={r.id} value={r.id}>
                <span className="flex items-center gap-1.5">
                  <span
                    className="w-2.5 h-2.5 rounded-full inline-block"
                    style={{ backgroundColor: r.color }}
                  />
                  {r.label}
                </span>
              </Radio.Button>
            ))}
          </Radio.Group>
        </div>
      )}

      {missingIds.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={`${missingIds.length} selected run${missingIds.length === 1 ? "" : "s"} could not be loaded (deleted?) and ${missingIds.length === 1 ? "was" : "were"} skipped.`}
        />
      )}
      {ready && !compatibility.ok && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          message="Heads up — these runs may not be directly comparable"
          description={
            <ul className="list-disc ml-4 mb-0">
              {compatibility.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          }
        />
      )}
    </div>
  );

  const body = !ready ? (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-12">
      <Empty
        description={
          <div className="max-w-md mx-auto">
            <p className="text-gray-700 font-medium mb-1">
              Compare benchmark runs side by side
            </p>
            <p className="text-gray-500 text-sm">
              Pick at least two runs above. We line them up by case so you can
              see exactly where one run beats another — verdicts, judge scores,
              latency, cost, and the input itself for visual judging.
            </p>
          </div>
        }
      />
    </div>
  ) : (
    <Spin spinning={loading && matrix.rows.length === 0}>
      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: "overview",
            label: (
              <span>
                <BarChartOutlined /> Overview
              </span>
            ),
            children: (
              <CompareOverview
                runs={runs}
                matrix={matrix}
                baselineId={baselineId}
                agents={agents}
              />
            ),
          },
          {
            key: "cases",
            label: (
              <span>
                <TableOutlined /> Cases{" "}
                <Tag
                  className="!ml-1 !mr-0"
                  color={
                    matrix.rows.some((r) => r.divergent) ? "orange" : "default"
                  }
                >
                  {matrix.rows.length}
                </Tag>
              </span>
            ),
            children: (
              <CompareCasesTable
                runs={runs}
                matrix={matrix}
                baselineId={baselineId}
                unalignable={matrix.unalignable}
              />
            ),
          },
        ]}
      />
    </Spin>
  );

  const main = (
    <div
      className={
        fullscreen
          ? "fixed inset-0 z-[1000] bg-gray-50 overflow-y-auto p-5 space-y-4"
          : "space-y-4"
      }
    >
      {fullscreen && (
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-800">
            Compare Benchmark Runs
          </h2>
          <Button
            icon={<FullscreenExitOutlined />}
            onClick={() => setFullscreen(false)}
          >
            Exit fullscreen
          </Button>
        </div>
      )}
      {toolbar}
      {body}
    </div>
  );

  return (
    <>
      {!fullscreen && (
        <Topbar
          title="Compare Benchmark Runs"
          breadcrumb={[{ title: "Benchmark", href: "/benchmark" }]}
        />
      )}
      {fullscreen ? main : <Content>{main}</Content>}
    </>
  );
}
